import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import {
	getMonitoringTraceability,
	inspectMonitoringSearch,
} from "../../../../services/monitoringApi";
import {
	VERDICT_LABEL,
	VERDICT_ORDER,
	VERDICT_TONE,
} from "./trafficShape";
import { fmtNum } from "./trafficUtils";
import { TrafficTableTh } from "./TrafficKpiCard";
import TrafficListDialog from "./TrafficListDialog";
import UrlCopyCell from "./UrlCopyCell";

const INLINE_ROWS = 8;

function VerdictPill({ v }) {
	const t = (v || "").toUpperCase();
	let cls = "monitoring__ai-traffic-verdict--unknown";
	if (t === "PASS") cls = "monitoring__ai-traffic-verdict--pass";
	else if (t === "FAIL") cls = "monitoring__ai-traffic-verdict--fail";
	else if (t === "NEUTRAL") cls = "monitoring__ai-traffic-verdict--neutral";
	return <span className={`monitoring__ai-traffic-verdict ${cls}`}>{t || "—"}</span>;
}

function fmtFreshnessSync(value) {
	if (!value) return "—";
	if (!/\d{4}-\d\d-\d\d/.test(String(value))) return value;
	return new Date(value).toLocaleString(undefined, {
		hour: "2-digit",
		minute: "2-digit",
		day: "2-digit",
		month: "short",
	});
}

export default function TraceabilityPanel({ websiteId, range, bots }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [inspectBusy, setInspectBusy] = useState(false);
	const [dialog, setDialog] = useState(null);

	const botsParam = bots?.length ? bots.join(",") : undefined;

	useEffect(() => {
		let cancelled = false;
		async function load() {
			setLoading(true);
			const res = await getMonitoringTraceability(websiteId, {
				start: range.start,
				end: range.end,
				assistants: botsParam,
			});
			if (cancelled) return;
			setData(res.ok ? res.traceability : null);
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId, range.start, range.end, botsParam]);

	async function handleInspect() {
		if (inspectBusy) return;
		setInspectBusy(true);
		const res = await inspectMonitoringSearch(websiteId);
		setInspectBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Inspection failed");
			return;
		}
		toast.success("URL inspection queued");
		setTimeout(() => {
			getMonitoringTraceability(websiteId, {
				start: range.start,
				end: range.end,
				assistants: botsParam,
			}).then((r) => {
				if (r.ok) setData(r.traceability);
			});
		}, 10000);
	}

	if (loading || !data) {
		return (
			<p className="monitoring__ai-traffic-empty mb-0">Loading traceability…</p>
		);
	}

	const cited = data.cited || {};
	const verdictTotal = Object.values(data.verdict_distribution || {}).reduce(
		(a, b) => a + b,
		0
	);
	const sitemap = data.sitemaps?.[0];
	const canonicalMismatch = data.canonical_mismatch || [];
	const crawledNotIndexable = data.crawled_not_indexable || { count: 0, items: [] };
	const rankingNotCited = data.ranking_not_cited || [];
	const citedNotRanking = data.cited_not_ranking || [];
	const recency = data.recency || [];
	const notIndexedBlocking = cited.not_indexed_blocking || [];
	const notIndexedAdvisory = cited.not_indexed_advisory ?? 0;
	const notIndexed = cited.not_indexed || [];

	return (
		<div className="monitoring__ai-traffic-panel">
			<div className="monitoring__ai-traffic-info-banner d-flex justify-content-between align-items-start gap-3">
				<p className="mb-0">
					<strong>
						Crawl, ranking, citation and visit figures follow the date and bot
						filters.
					</strong>{" "}
					Index verdicts, canonical checks and sitemap counts come from a
					point-in-time inspection sample, so those cards do not change with the
					range.
				</p>
				<Button
					size="sm"
					color="primary"
					className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-btn-compact"
					disabled={inspectBusy}
					onClick={handleInspect}
				>
					{inspectBusy ? "Inspecting…" : "Inspect more URLs"}
				</Button>
			</div>

			<div className="monitoring__ai-traffic-kpi-grid monitoring__ai-traffic-kpi-grid--4">
				<div className="monitoring__ai-traffic-kpi-card">
					<div className="monitoring__ai-traffic-kpi-label">
						Index coverage of AI-cited pages{" "}
						<span className="text-primary">R1</span>
					</div>
					<div className="monitoring__ai-traffic-kpi-value-row">
						<span className="monitoring__ai-traffic-headline-value">
							{cited.indexed ?? 0}
						</span>
						<span className="text-muted">of {cited.total ?? 0} cited</span>
						<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--gsc">
							GSC
						</span>
					</div>
					{notIndexedBlocking.length > 0 && (
						<button
							type="button"
							className="monitoring__ai-traffic-trace-link monitoring__ai-traffic-trace-link--danger"
							onClick={() =>
								setDialog({
									title: `${notIndexedBlocking.length} — cited by Gemini but not indexed`,
									detail:
										"Gemini answers from Google's index, so these cannot appear on Google's AI surfaces.",
									rows: notIndexedBlocking,
									columns: [
										{
											key: "url",
											label: "Page",
											cellClass: "monitoring__ai-traffic-url-cell",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "cov",
											label: "Coverage",
											render: (r) => r.coverage_state ?? "—",
										},
									],
								})
							}
						>
							{notIndexedBlocking.length} cited by Gemini, NOT indexed →
						</button>
					)}
					{notIndexedAdvisory > 0 && (
						<button
							type="button"
							className="monitoring__ai-traffic-trace-link"
							onClick={() =>
								setDialog({
									title: `${notIndexedAdvisory} — cited by direct-crawling AI, not indexed`,
									detail:
										"These engines fetched the pages themselves, so the citations work. The pages just cannot surface in Google's AI results.",
									rows: notIndexed.filter(
										(r) => !(r.cited_by ?? []).includes("Gemini")
									),
									columns: [
										{
											key: "url",
											label: "Page",
											cellClass: "monitoring__ai-traffic-url-cell",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "by",
											label: "Cited by",
											render: (r) => (r.cited_by ?? []).join(", ") || "—",
										},
									],
								})
							}
						>
							{notIndexedAdvisory} cited by direct-crawling AI, not indexed →
						</button>
					)}
					{(cited.unknown ?? 0) > 0 && (
						<div className="monitoring__ai-traffic-kpi-hint">
							{cited.unknown} not yet inspected
						</div>
					)}
				</div>

				<div className="monitoring__ai-traffic-kpi-card">
					<div className="monitoring__ai-traffic-kpi-label">
						Canonical mismatch <span className="text-primary">R4</span>
					</div>
					<div className="monitoring__ai-traffic-kpi-value-row">
						<span className="monitoring__ai-traffic-headline-value">
							{canonicalMismatch.length}
						</span>
						<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--gsc">
							GSC
						</span>
					</div>
					<div className="monitoring__ai-traffic-kpi-hint">
						Google chose a different canonical — a common reason a cited URL
						isn't the ranking one.
					</div>
					{canonicalMismatch.length > 0 && (
						<button
							type="button"
							className="monitoring__ai-traffic-trace-link monitoring__ai-traffic-trace-link--primary"
							onClick={() =>
								setDialog({
									title: `${canonicalMismatch.length} — canonical mismatch`,
									detail: "Google's canonical differs from the page's own.",
									rows: canonicalMismatch,
									columns: [
										{
											key: "url",
											label: "Page",
											cellClass: "monitoring__ai-traffic-url-cell",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "gc",
											label: "Google canonical",
											render: (r) => (
												<UrlCopyCell
													url={r.google_canonical}
													label="Copy canonical"
												/>
											),
										},
									],
								})
							}
						>
							View →
						</button>
					)}
				</div>

				<div className="monitoring__ai-traffic-kpi-card">
					<div className="monitoring__ai-traffic-kpi-label">
						Crawled but non-indexable <span className="text-primary">R5</span>
					</div>
					<div className="monitoring__ai-traffic-kpi-value-row">
						<span
							className={`monitoring__ai-traffic-headline-value${
								crawledNotIndexable.count
									? " monitoring__ai-traffic-kpi-value--danger"
									: ""
							}`}
						>
							{crawledNotIndexable.count ?? 0}
						</span>
						<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--gsc">
							GSC
						</span>
						<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--cf">
							CF
						</span>
					</div>
					<div className="monitoring__ai-traffic-kpi-hint">noindex or robots blocked</div>
					{crawledNotIndexable.count > 0 && (
						<button
							type="button"
							className="monitoring__ai-traffic-trace-link monitoring__ai-traffic-trace-link--primary"
							onClick={() =>
								setDialog({
									title: `${crawledNotIndexable.count} — crawled but non-indexable`,
									detail:
										"AI bots fetch these but Google is barred from indexing them.",
									rows: crawledNotIndexable.items || [],
									columns: [
										{
											key: "url",
											label: "Page",
											cellClass: "monitoring__ai-traffic-url-cell",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "why",
											label: "Reason",
											render: (r) =>
												r.robots_state ||
												r.indexing_state ||
												r.coverage_state ||
												"—",
										},
									],
								})
							}
						>
							View →
						</button>
					)}
				</div>

				<div className="monitoring__ai-traffic-kpi-card">
					<div className="monitoring__ai-traffic-kpi-label">
						Sitemap indexed <span className="text-primary">R6</span>
					</div>
					{sitemap ? (
						<>
							<div className="monitoring__ai-traffic-kpi-value-row">
								<span className="monitoring__ai-traffic-headline-value">
									{fmtNum(sitemap.indexed)}
								</span>
								<span className="text-muted">
									of {fmtNum(sitemap.submitted)} submitted
								</span>
								<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--gsc">
									GSC
								</span>
							</div>
							<div className="monitoring__ai-traffic-status-bar-track mt-2">
								<div
									className="monitoring__ai-traffic-status-bar-fill monitoring__ai-traffic-status-bar-fill--ok"
									style={{
										width: `${
											sitemap.submitted
												? (sitemap.indexed / sitemap.submitted) * 100
												: 0
										}%`,
									}}
								/>
							</div>
							{sitemap.path && (
								<div
									className="monitoring__ai-traffic-sitemap-path"
									title={sitemap.path}
								>
									{sitemap.path}
								</div>
							)}
						</>
					) : (
						<p className="monitoring__ai-traffic-empty mb-0 mt-2">
							No sitemap reported by Search Console.
						</p>
					)}
				</div>
			</div>

			<div className="monitoring__ai-traffic-chart-grid">
				<div className="monitoring__ai-traffic-kpi-card">
					<h3 className="monitoring__ai-traffic-section-title">
						Indexing verdict distribution{" "}
						<span className="text-primary font-monospace">R2</span>
					</h3>
					{verdictTotal === 0 ? (
						<p className="monitoring__ai-traffic-empty mb-0">
							No URLs inspected yet — use &quot;Inspect more URLs&quot;.
						</p>
					) : (
						<>
							<p className="monitoring__ai-traffic-section-note mb-2">
								Index state known for{" "}
								<strong>{data.index_state_known ?? 0}</strong> of{" "}
								<strong>{fmtNum(data.crawled_total ?? 0)}</strong> crawled pages.
								A sample, not a census.
							</p>
							{VERDICT_ORDER.filter((v) => data.verdict_distribution?.[v]).map(
								(v) => {
									const pct =
										(data.verdict_distribution[v] / verdictTotal) * 100;
									return (
										<div key={v} className="monitoring__ai-traffic-status-row">
											<div className="monitoring__ai-traffic-status-row-head">
												<span>{VERDICT_LABEL[v] ?? v}</span>
												<span>
													{pct.toFixed(1)}% ({data.verdict_distribution[v]})
												</span>
											</div>
											<div className="monitoring__ai-traffic-status-bar-track">
												<div
													className={`monitoring__ai-traffic-status-bar-fill ${VERDICT_TONE[v]}`}
													style={{ width: `${pct}%` }}
												/>
											</div>
										</div>
									);
								}
							)}
						</>
					)}
				</div>

				<div className="monitoring__ai-traffic-kpi-card">
					<h3 className="monitoring__ai-traffic-section-title">
						Source freshness &amp; lineage{" "}
						<span className="text-primary font-monospace">R12</span>
					</h3>
					<p className="monitoring__ai-traffic-section-note mb-2">
						What each source can actually tell us, and how current it is.
					</p>
					<div className="monitoring__table-scroll">
						<table className="monitoring__table monitoring__ai-traffic-freshness-table">
							<thead>
								<tr>
									<TrafficTableTh>Source</TrafficTableTh>
									<TrafficTableTh>Held</TrafficTableTh>
									<TrafficTableTh>Latency</TrafficTableTh>
									<TrafficTableTh>Last sync</TrafficTableTh>
								</tr>
							</thead>
							<tbody>
								{(data.source_freshness || []).map((f) => (
									<tr key={f.source}>
										<td className="monitoring__ai-traffic-source-cell">
											{f.source}
										</td>
										<td className="text-muted">{f.held ?? "—"}</td>
										<td className="text-muted">{f.latency ?? "—"}</td>
										<td className="text-muted">
											{fmtFreshnessSync(f.last_sync)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div className="monitoring__ai-traffic-chart-grid">
				<div className="monitoring__ai-traffic-kpi-card">
					<h3 className="monitoring__ai-traffic-section-title">
						Ranking but not cited{" "}
						<span className="text-primary font-monospace">R9</span>
					</h3>
					<p className="monitoring__ai-traffic-section-note mb-2">
						Strong in Google, invisible to AI engines. The prioritised AEO work
						list.
					</p>
					<div className="monitoring__table-scroll">
						<table className="monitoring__table">
							<thead>
								<tr>
									<TrafficTableTh>Page</TrafficTableTh>
									<TrafficTableTh className="monitoring__th-num">Pos.</TrafficTableTh>
									<TrafficTableTh className="monitoring__th-num">Clicks</TrafficTableTh>
									<TrafficTableTh className="monitoring__th-num">Index</TrafficTableTh>
								</tr>
							</thead>
							<tbody>
								{rankingNotCited.slice(0, INLINE_ROWS).map((r) => (
									<tr key={r.url}>
										<td>
											<UrlCopyCell url={r.url} />
										</td>
										<td className="monitoring__td-num">{r.position}</td>
										<td className="monitoring__td-num">{r.clicks}</td>
										<td className="monitoring__td-num">
											<VerdictPill v={r.verdict} />
										</td>
									</tr>
								))}
								{rankingNotCited.length === 0 && (
									<tr>
										<td
											colSpan={4}
											className="monitoring__ai-traffic-empty text-center py-4"
										>
											None.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					{rankingNotCited.length > INLINE_ROWS && (
						<Button
							size="sm"
							color="primary"
							className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all w-100 mt-2"
							onClick={() =>
								setDialog({
									title: `${rankingNotCited.length} — ranking but not cited`,
									detail:
										"Strong in Google, never cited by AI — the AEO work list.",
									rows: rankingNotCited,
									columns: [
										{
											key: "url",
											label: "Page",
											cellClass: "monitoring__ai-traffic-url-cell",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "pos",
											label: "Position",
											align: "right",
											render: (r) => r.position,
										},
										{
											key: "clicks",
											label: "Clicks",
											align: "right",
											render: (r) => r.clicks,
										},
										{
											key: "verdict",
											label: "Index",
											align: "right",
											render: (r) => r.verdict ?? "—",
										},
									],
								})
							}
						>
							View all {rankingNotCited.length} pages
						</Button>
					)}
				</div>

				<div className="monitoring__ai-traffic-kpi-card">
					<h3 className="monitoring__ai-traffic-section-title">
						Cited but not ranking{" "}
						<span className="text-primary font-monospace">R8</span>
					</h3>
					<p className="monitoring__ai-traffic-section-note mb-2">
						AI engines quote these; Google shows them to nobody. Often thin or
						orphaned.
					</p>
					<div className="monitoring__table-scroll monitoring__table-scroll--fit">
						<table className="monitoring__table monitoring__ai-traffic-cited-table">
							<thead>
								<tr>
									<TrafficTableTh>Page</TrafficTableTh>
									<TrafficTableTh>Cited by</TrafficTableTh>
									<TrafficTableTh className="monitoring__th-num">Impr.</TrafficTableTh>
									<TrafficTableTh className="monitoring__th-num">Index</TrafficTableTh>
								</tr>
							</thead>
							<tbody>
								{citedNotRanking.slice(0, INLINE_ROWS).map((r) => (
									<tr key={r.url}>
										<td>
											<UrlCopyCell url={r.url} />
										</td>
										<td className="text-muted">
											{(r.cited_by || []).join(", ") || "—"}
										</td>
										<td className="monitoring__td-num">{r.impressions}</td>
										<td className="monitoring__td-num">
											<VerdictPill v={r.verdict} />
										</td>
									</tr>
								))}
								{citedNotRanking.length === 0 && (
									<tr>
										<td
											colSpan={4}
											className="monitoring__ai-traffic-empty text-center py-4"
										>
											None.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
					{citedNotRanking.length > INLINE_ROWS && (
						<Button
							size="sm"
							color="primary"
							className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all w-100 mt-2"
							onClick={() =>
								setDialog({
									title: `${citedNotRanking.length} — cited but not ranking`,
									detail: "AI cites these; Google shows them to nobody.",
									rows: citedNotRanking,
									columns: [
										{
											key: "url",
											label: "Page",
											render: (r) => <UrlCopyCell url={r.url} />,
										},
										{
											key: "by",
											label: "Cited by",
											render: (r) => (r.cited_by || []).join(", ") || "—",
										},
										{
											key: "impr",
											label: "Impr.",
											align: "right",
											render: (r) => r.impressions,
										},
										{
											key: "verdict",
											label: "Index",
											align: "right",
											render: (r) => r.verdict ?? "—",
										},
									],
								})
							}
						>
							View all {citedNotRanking.length} pages
						</Button>
					)}
				</div>
			</div>

			<div className="monitoring__ai-traffic-kpi-card">
				<h3 className="monitoring__ai-traffic-section-title">
					Googlebot vs AI-bot crawl recency{" "}
					<span className="text-primary font-monospace">R3</span>
				</h3>
				<p className="monitoring__ai-traffic-section-note mb-2">
					Different clocks, so compared as recency bands rather than timestamps.
				</p>
				<div className="monitoring__table-scroll">
					<table className="monitoring__table monitoring__ai-traffic-recency-table">
						<thead>
							<tr>
								<TrafficTableTh>Page</TrafficTableTh>
								<TrafficTableTh>Googlebot</TrafficTableTh>
								<TrafficTableTh>AI bots</TrafficTableTh>
								<TrafficTableTh className="monitoring__th-num">Index</TrafficTableTh>
								<TrafficTableTh className="monitoring__th-num">Canonical</TrafficTableTh>
							</tr>
						</thead>
						<tbody>
							{recency.slice(0, INLINE_ROWS).map((r) => (
								<tr key={r.url}>
									<td>
										<UrlCopyCell url={r.url} />
									</td>
									<td className="text-muted">{r.googlebot ?? "—"}</td>
									<td className="text-muted">{r.ai_bots ?? "—"}</td>
									<td className="monitoring__td-num">
										<VerdictPill v={r.verdict} />
									</td>
									<td
										className={`monitoring__td-num${
											r.canonical === "differs"
												? " monitoring__ai-traffic-canonical--warn"
												: " text-muted"
										}`}
									>
										{r.canonical ?? "—"}
									</td>
								</tr>
							))}
							{recency.length === 0 && (
								<tr>
									<td
										colSpan={5}
										className="monitoring__ai-traffic-empty text-center py-4"
									>
										No inspected pages yet.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
				{recency.length > INLINE_ROWS && (
					<Button
						size="sm"
						color="primary"
						className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all w-100 mt-2"
						onClick={() =>
							setDialog({
								title: `${recency.length} — pages with known index state`,
								detail:
									"Googlebot vs AI-bot recency and index verdict.",
								rows: recency,
								columns: [
									{
										key: "url",
										label: "Page",
										cellClass: "monitoring__ai-traffic-url-cell",
										render: (r) => <UrlCopyCell url={r.url} />,
									},
									{
										key: "gb",
										label: "Googlebot",
										render: (r) => r.googlebot ?? "—",
									},
									{
										key: "ai",
										label: "AI bots",
										render: (r) => r.ai_bots ?? "—",
									},
									{
										key: "v",
										label: "Index",
										render: (r) => r.verdict ?? "—",
									},
								],
							})
						}
					>
						View all {recency.length} pages with known index state
					</Button>
				)}
			</div>

			{dialog && (
				<TrafficListDialog
					title={dialog.title}
					detail={dialog.detail}
					rows={dialog.rows}
					columns={dialog.columns}
					rowKey={(r) => r.url}
					searchText={(r) => r.url}
					copyText={(r) => r.url}
					unit="URL"
					unitPlural="URIs"
					pageSize={10}
					onClose={() => setDialog(null)}
				/>
			)}
		</div>
	);
}
